import { StrictMode } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router';
import { ToastContainer } from 'react-toastify';

import App from '@/App';
import { AppRedirectHandler } from '@/AppRedirectHandler';
import { oidcConfig } from '@/auth';
import { ErrorBoundary } from '@/components';
import { APP_BASE_URL, APP_ROOT_ELEMENT_ID } from '@/constants';
import { GlobalProvider } from '@/context';
import { theme } from '@/styles/theme';

// fonts initialization
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/500.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';

// global styles initialization
import '@/styles/global.scss';

const rootElement = document.getElementById(APP_ROOT_ELEMENT_ID ?? 'root');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// all global providers should be placed here
createRoot(rootElement!).render(
  <StrictMode>
    <AppRedirectHandler>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <AuthProvider {...oidcConfig}>
          <BrowserRouter basename={APP_BASE_URL}>
            <QueryClientProvider client={queryClient}>
              <ErrorBoundary>
                <GlobalProvider>
                  <App />
                  <ToastContainer
                    position="bottom-right"
                    pauseOnHover
                    hideProgressBar
                  />
                </GlobalProvider>
              </ErrorBoundary>
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
              />
            </QueryClientProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </AppRedirectHandler>
  </StrictMode>,
);
